'use client';

/**
 * NeonLayer — the artwork's own light, alive.
 *
 * OWNED BY THE LIGHT STAGE. Renders pixel-aligned over the reference PNG using
 * public/studyninja/light/<scene>-lime.png as a CSS mask (see
 * scripts/studyninja-light-masks.py). Pointer-events: none; only transform and
 * opacity animate. Structure and timings follow MOTION-SPEC §4:
 *
 *   glow (blur, breathe, 3 px float) → fill (masked accent)
 *   line (masked) → band | sector (travel), lens (pointer), spark (click)
 *
 * The host wrapper (`data-testid="sn-neon"`, shell) supplies mix-blend-mode:
 * screen; pass `blend` when rendering the layer without that wrapper.
 *
 * Consumed CSS custom properties (written by the shell on an ancestor, never
 * through React state):
 *   --sn-mx / --sn-my      pointer position 0..1 inside the canvas (lens)
 *   --sn-lens-on           1 while the pointer is over the viewport, else 0
 *   --sn-dx / --sn-dy      parallax deltas −1..1 (glow floats 3 px with them)
 *   --sn-fx / --sn-fy      last click point as percentages, e.g. "43.20%" (spark)
 *   --sn-ease-breathe / --sn-ease-enter   optional easing tokens
 * Host attributes honoured: [data-boot] (travel paused, power-on delay 420 ms),
 * [data-reduced] (static bloom), [data-input="coarse"] (no lens).
 */

import { useContext, useEffect, useState, type CSSProperties } from 'react';
import type { ReferenceSceneId } from '../../reference/types';
import { LivingContext } from '../context';
import { SCENE_CANVAS } from '../types';
import { TRAVEL, TRAVEL_RESUME_MS, travelVector } from './travel';
import styles from './NeonLayer.module.css';

export interface LightMeta {
  src: string;
  width: number;
  height: number;
}

export const LIGHT_META: Record<ReferenceSceneId, LightMeta> = Object.fromEntries(
  (Object.keys(SCENE_CANVAS) as ReferenceSceneId[]).map((scene) => [
    scene,
    { src: `/studyninja/light/${scene}-lime.png`, ...SCENE_CANVAS[scene] },
  ]),
) as Record<ReferenceSceneId, LightMeta>;

/** 'off' while the scene is transitioning in (layer invisible), 'on' afterwards (power-on flicker, then travel). */
export type NeonPhase = 'off' | 'on';
export const NEON_PHASES = ['off', 'on'] as const satisfies readonly NeonPhase[];

/** 'full' = bloom + travel + lens + spark; 'breathe' = bloom only (mobile strip slides). */
export type NeonVariant = 'full' | 'breathe';

export interface NeonLayerProps {
  scene: ReferenceSceneId;
  /** Defaults to 'on' so a layer mounted without a transition still powers on once. */
  phase?: NeonPhase;
  variant?: NeonVariant;
  /** Increment on every hotspot / inset click after writing --sn-fx/--sn-fy; 0 renders no spark. */
  sparkTick?: number;
  /** Apply mix-blend-mode: screen on the layer itself (only when no sn-neon wrapper does it). */
  blend?: boolean;
}

export default function NeonLayer({ scene, phase = 'on', variant = 'full', sparkTick = 0, blend = false }: NeonLayerProps) {
  const living = useContext(LivingContext);
  const reduced = living?.reducedMotion ?? false;
  const touch = living?.touch ?? false;
  const meta = LIGHT_META[scene];
  const travel = TRAVEL[scene];
  const full = variant === 'full';

  // Travel resumes TRAVEL_RESUME_MS after the scene lands (phase 'on'); a
  // phase back to 'off' re-arms the wait. Derived-state reset happens in
  // render, the timer in an effect with strict cleanup.
  const [armed, setArmed] = useState(false);
  const [seenPhase, setSeenPhase] = useState(phase);
  if (seenPhase !== phase) {
    setSeenPhase(phase);
    if (phase === 'off') setArmed(false);
  }
  useEffect(() => {
    if (phase !== 'on' || reduced || !full) return;
    const id = window.setTimeout(() => setArmed(true), TRAVEL_RESUME_MS);
    return () => window.clearTimeout(id);
  }, [phase, reduced, full]);

  // The spark unmounts itself when its 640 ms flare ends.
  const [sparkDone, setSparkDone] = useState(0);
  const spark = full && !reduced && sparkTick > 0 && sparkDone !== sparkTick;

  const style: Record<string, string> = {
    '--sn-mask': `url("${meta.src}")`,
    '--sn-travel-ms': `${travel.period}ms`,
  };
  if (travel.kind === 'band') {
    const { tx, ty } = travelVector(travel.angle);
    style['--sn-travel'] = `${travel.angle}deg`;
    style['--sn-tx'] = String(tx);
    style['--sn-ty'] = String(ty);
  } else {
    style['--sn-cx'] = `${travel.cx}%`;
    style['--sn-cy'] = `${travel.cy}%`;
  }

  return (
    <div
      className={styles.layer}
      style={style as CSSProperties}
      data-testid="sn-neon-layer"
      data-scene={scene}
      data-phase={phase}
      data-variant={variant}
      data-travel={full && phase === 'on' && armed && !reduced ? 'on' : 'off'}
      data-lens={full && !touch && !reduced ? 'on' : 'off'}
      data-reduced={reduced ? 'true' : undefined}
      data-blend={blend ? 'screen' : undefined}
      aria-hidden="true"
    >
      <div className={styles.glow} data-part="glow">
        <div className={styles.fill} data-part="fill" />
      </div>
      {full && (
        <div className={styles.line} data-part="line">
          {travel.kind === 'band'
            ? <span className={styles.band} data-part="band" />
            : <span className={styles.sector} data-part="sector" />}
          <span className={styles.lens} data-part="lens" />
          {spark && (
            <span
              key={sparkTick}
              className={styles.spark}
              data-part="spark"
              onAnimationEnd={() => setSparkDone(sparkTick)}
            />
          )}
        </div>
      )}
    </div>
  );
}
